import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { addEvent, removeEvent, addClass, removeClass } from '../../utils/dom';
import s from './Slider.scss';

class Slider extends Component {
	static getDragX(e) {
		return e.touches[0].pageX;
	}

	static getIndex(infinite, childrenLength, index) {
		if (infinite && index < -1) {
			index = -1;
		} else if (infinite && index > childrenLength) {
			index = childrenLength;
		} else if (!infinite && index < 0) {
			index = 0;
		} else if (!infinite && index >= childrenLength) {
			index = childrenLength - 1;
		}
		return index;
	}

	constructor(props) {
		super(props);

		this.state = {
			dragStart: 0,
			dragStartTime: new Date(),
			index: props.startIndex || 0,
			lastIndex: props.startIndex || 0,
			transition: false,
			disabled: props.children.length === 1,
		};

		this.slider = React.createRef();
		this.sliderInner = React.createRef();
		this.sliderNav = React.createRef();
	}

	componentDidMount() {
		const { screenMaxWidthDisable, screenMinWidthDisable, children, autoplay, autoplayDelay, transitionType } = this.props;
		let { index: fadeIndex = 0 } = this.state;

		const autoplayFunction = () => {
			if (transitionType !== 'fade') {
				let nextIndex = this.state.index + 1;
				nextIndex = Slider.getIndex(this.props.infinite, this.props.children.length, nextIndex);
				this.goToSlide(nextIndex);
			} else if (this.sliderInner.current && this.sliderNav.current) {
				if (this.sliderInner.current?.children?.[0]?.children?.[fadeIndex]) removeClass(this.sliderInner.current.children[0].children[fadeIndex], 'u_selected');
				if (this.sliderNav.current?.children?.[fadeIndex - 1]) removeClass(this.sliderNav.current.children[fadeIndex - 1], 'u_selected');
				fadeIndex = (fadeIndex + 1) % this.props.children.length;
				if (this.sliderInner.current?.children?.[0]?.children?.[fadeIndex]) addClass(this.sliderInner.current.children[0].children[fadeIndex], 'u_selected');
				if (this.sliderNav.current?.children?.[fadeIndex - 1]) addClass(this.sliderNav.current.children[fadeIndex - 1], 'u_selected');
			}
		};

		if (autoplay && children.length > 1) {
			if (this.sliderInner.current?.children?.[0]?.children?.[fadeIndex]) addClass(this.sliderInner.current.children[0].children[fadeIndex], 'u_selected');
			if (this.sliderNav.current?.children?.[fadeIndex - 1]) addClass(this.sliderNav.current.children[fadeIndex - 1], 'u_selected');
			this.autoplayDelayPointer = window.setTimeout(() => {
				this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);
			}, autoplayDelay ? (20e3 - parseInt(autoplay, 10)) : 0);
		}

		if ((screenMaxWidthDisable !== false || screenMinWidthDisable !== false) && children.length > 1) {
			let isDisabled = false;

			const resizeEvent = () => {
				const width = window.innerWidth;
				if (
					children.length <= 1
					|| (screenMaxWidthDisable !== false && !isDisabled && width > screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && !isDisabled && width < screenMinWidthDisable)
				) {
					// Only one child or less || Screen is over max width || Screen is under min width -> disable
					isDisabled = true;

					if (autoplayDelay) window.clearInterval(this.autoplayDelayPointer);
					if (autoplay) window.clearInterval(this.autoplayPointer);
					this.setState({
						disabled: true,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
				} else if (
					(screenMaxWidthDisable !== false && isDisabled && width <= screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && isDisabled && width >= screenMinWidthDisable)
				) {
					isDisabled = false;

					if (autoplayDelay) window.clearInterval(this.autoplayDelayPointer);
					if (autoplay) this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);
					this.setState({
						disabled: false,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
					addEvent(document.body, 'touchmove.slider', (e) => {
						if (this.sliderInner.current && this.sliderInner.current.contains(e.target)) {
							if (Math.abs(e.touches[0].pageX - this.state.dragStart) > 20) {
								e.preventDefault();
							}
						}
					}, { passive: false });
				}
			};

			addEvent(window, 'resize.slider', () => {
				window.requestAnimationFrame(resizeEvent);
			}, { passive: false });

			resizeEvent();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location && prevProps.location !== this.props.location) this.goToSlide(0, false, false);
	}

	componentWillUnmount() {
		removeEvent(document.body, 'touchmove.slider', { passive: false });
		if ((this.props.screenMaxWidthDisable !== false || this.props.screenMinWidthDisable !== false) && this.props.children.length > 1) removeEvent(window, 'resize.slider', { passive: false });
		if (this.props.autoplayDelay) window.clearInterval(this.autoplayDelayPointer);
		if (this.props.autoplay) window.clearInterval(this.autoplayPointer);
	}

	handleDragStart = (e) => {
		const x = Slider.getDragX(e);

		if (this.props.autoplay) window.clearInterval(this.autoplayPointer);
		if (this.props.autoplayDelay) window.clearInterval(this.autoplayDelayPointer);

		this.setState({
			dragStart: x,
			dragStartTime: new Date(),
			transition: false,
			slideWidth: this.slider.current.offsetWidth,
		});
	}

	handleDragMove = (e) => {
		const { dragStart, lastIndex, slideWidth } = this.state;

		const x = Slider.getDragX(e);
		const offset = dragStart - x;
		const percentageOffset = offset / slideWidth;
		const newIndex = lastIndex + percentageOffset;
		const SCROLL_OFFSET_TO_STOP_SCROLL = 30;

		// Stop scrolling if you slide more than 30 pixels
		if (Math.abs(offset) > SCROLL_OFFSET_TO_STOP_SCROLL) {
			e.stopPropagation();
			e.preventDefault();
		}

		this.setState({
			index: newIndex,
		});
	}

	handleDragEnd = () => {
		const { infinite, children } = this.props;
		const { dragStartTime, index, lastIndex } = this.state;

		const timeElapsed = new Date().getTime() - dragStartTime.getTime();
		const offset = lastIndex - index;
		const velocity = Math.round((offset / timeElapsed) * 10000);

		let newIndex = Math.round(index);

		if (Math.abs(velocity) > 5) {
			newIndex = velocity < 0 ? lastIndex + 1 : lastIndex - 1;
		}

		newIndex = Slider.getIndex(infinite, children.length, newIndex);

		this.setState({
			dragStart: 0,
			index: newIndex,
			lastIndex: newIndex,
			transition: true,
		});
	}

	handleTransitionEnd = () => {
		const { infinite, children } = this.props;
		const { index } = this.state;
		let newIndex = index;

		if (infinite) {
			let updateState = false;

			if (newIndex === -1) {
				updateState = true;
				newIndex = children.length - 1;
			} else if (newIndex === children.length) {
				updateState = true;
				newIndex = 0;
			}

			if (updateState) {
				this.setState({
					index: newIndex,
					lastIndex: newIndex,
					transition: false,
				});
			}
		}
	}

	handleClick = (e, dir) => {
		e.preventDefault();
		const { lastIndex } = this.state;

		this.goToSlide(lastIndex + dir, e);
	}

	goToSlide = (index, e, transition = true) => {
		const { infinite, children, loop } = this.props;

		if (e) {
			e.preventDefault();
			e.stopPropagation();

			if (this.props.autoplay) window.clearInterval(this.autoplayPointer);
			if (this.props.autoplayDelay) window.clearInterval(this.autoplayDelayPointer);
		}

		if (!infinite && index < 0) {
			index = loop ? children.length - 1 : 0;
		} else if (!infinite && index >= children.length) {
			index = loop ? 0 : children.length - 1;
		} else if (infinite && index <= -1) {
			window.setTimeout(() => {
				this.setState({
					index: children.length - 1,
					lastIndex: children.length - 1,
					transition: false,
				});
			}, 300);
			index = -1;
		} else if (infinite && index >= children.length) {
			window.setTimeout(() => {
				this.setState({
					index: 0,
					lastIndex: 0,
					transition: false,
				});
			}, 300);
			index = children.length;
		}

		this.setState({
			index,
			lastIndex: index,
			transition,
		});
	}

	createThumbs(children) {
		return React.Children.map(children, (child) => {
			if (!React.isValidElement(child)) return child;
			const childProps = {};
			if (child.type === 'img') childProps.src = child.props.src.replace('-zoom', '-thumb');
			childProps.children = this.createThumbs(child.props.children);
			return React.cloneElement(child, childProps);
		});
	}

	renderNav() {
		const { children } = this.props;
		const { lastIndex } = this.state;

		const nav = children.map((slide, i) => {
			const buttonClasses = i === lastIndex ? [s.slider_navButton, s.slider_navButton__active] : [s.slider_navButton];
			return (
				<button type="button" className={buttonClasses.join(' ')} key={`pagination-${i}`} onClick={(e) => this.goToSlide(i, e)} aria-labelledby={`Slider pagination ${i}`} />
			);
		});

		return (
			<div className={s.slider_nav} ref={this.sliderNav}>{ nav }</div>
		);
	}

	renderArrows() {
		const { children, showNav, loop, infinite } = this.props;
		const { lastIndex } = this.state;
		const arrowsClasses = showNav ? [s.slider_arrows] : [s.slider_arrows, s.slider_arrows__noNav];

		return (
			<div className={arrowsClasses.join(' ')}>
				{ loop || infinite || lastIndex > 0 ? <button type="button" className={[s.slider_arrow, s.slider_arrow__left].join(' ')} onClick={(e) => this.goToSlide(lastIndex - 1, e)} aria-labelledby="Slider arrow left" /> : null }
				{ loop || infinite || lastIndex < children.length - 1 ? <button type="button" className={[s.slider_arrow, s.slider_arrow__right].join(' ')} onClick={(e) => this.goToSlide(lastIndex + 1, e)} aria-labelledby="Slider arrow right" /> : null }
			</div>
		);
	}

	renderThumbs() {
		const { index } = this.state;
		const thumbChildren = this.createThumbs(this.props.children);

		if (thumbChildren.length === 0) return null;

		return (
			<div className={[s.slider_thumbs, 'u_slideThumbs'].join(' ')}>
				<div className={s.slider_thumbs_inner}>
					{
						thumbChildren.map((child, i) => <a key={`${child.key}Thumb`} className={[s.slider_thumbs_inner_thumb, (i === index ? s.slider_thumbs_inner_thumb__selected : null)].join(' ')} href="#link" onClick={(e) => this.goToSlide(i, e)}>{ child }</a>)
					}
				</div>
			</div>
		);
	}

	render() {
		const { children, showArrows, showNav, adaption, infinite, thumbs, interactionTypes, transitionType } = this.props;
		const { index, transition, disabled } = this.state;
		const numberSlides = infinite && !disabled ? children.length + 2 : children.length;

		const slidesStyles = {};
		if (!disabled && transitionType !== 'fade') {
			slidesStyles.width = `${100 * numberSlides}%`;
			slidesStyles.transform = `translateX(${-1 * (infinite && children.length > 1 ? index + 1 : index) * (100 / numberSlides)}%)`;
		}

		const slidesClasses = [s.slider_slides, 'u_slideMain'];
		if (transition) slidesClasses.push(s.slider_slides__transition);

		const sliderClasses = [s.slider];
		if (adaption !== false) sliderClasses.push(s[`slider__${adaption}`]);
		if (transitionType === 'fade') sliderClasses.push(s.slider__fade);

		let slideElements = children;
		if (infinite && !disabled) {
			const startElement = React.Children.toArray(children).slice(-1)[0];
			const lastElement = React.Children.toArray(children).slice(0, 1)[0];

			slideElements = [
				React.cloneElement(startElement, { key: 'startElement' }),
				...React.Children.map(children, (child) => {
					if (child.props['data-film']) {
						const filmLink = child.props['data-film'];
						return React.cloneElement(child, {
							children: <video src={filmLink} autoPlay loop playsInline muted />,
						});
					}
					return child;
				}),
				React.cloneElement(lastElement, { key: 'lastElement' }),
			];
		}

		const interactionEvents = {};
		if (!disabled) {
			if (interactionTypes.indexOf('touch') !== -1) {
				interactionEvents.onTouchStart = this.handleDragStart;
				interactionEvents.onTouchMove = this.handleDragMove;
				interactionEvents.onTouchEnd = this.handleDragEnd;
				interactionEvents.onTransitionEnd = this.handleTransitionEnd;
			}
			if (interactionTypes.indexOf('click') !== -1) {
				interactionEvents.onClick = (e) => this.handleClick(e, 1);
			}
			if (interactionTypes.indexOf('rightClick') !== -1) {
				interactionEvents.onContextMenu = (e) => this.handleClick(e, -1);
			}
		}

		return (
			<div className={sliderClasses.join(' ')} ref={this.slider}>
				{ showArrows && !disabled ? this.renderArrows() : null }
				{ showNav && !disabled ? this.renderNav() : null }
				<div className={s.slider_inner} ref={this.sliderInner} {...interactionEvents}>
					<div className={slidesClasses.join(' ')} style={slidesStyles}>
						{ slideElements }
					</div>
				</div>
				{ thumbs ? this.renderThumbs() : null }
			</div>
		);
	}
}

Slider.defaultProps = {
	loop: true, // false or true
	selected: 0,
	startIndex: 0,
	infinite: true, // false or true
	autoplay: false, // false or delay in ms before slide
	autoplayDelay: false, // false or true
	thumbs: false, // false or true
	showArrows: true, // false or true
	showNav: true, // false or true
	adaption: false, // false or string
	transitionType: 'slide',
	transitionSpeed: 300,
	interactionTypes: ['touch'], // Array of strings - touch, click, rightClick
	screenMinWidthDisable: false, // false or integer - Disable slider under min width
	screenMaxWidthDisable: false, // false or integer - Disable slider over max width
};

export default hot(module)(Slider);
